/**
 * 虚拟滚动卡片高度计算器
 *
 * 用于为 @tanstack/virtual-core 的 estimateSize 提供每个卡片的精确高度，
 * 避免使用固定的 itemHeight 估计值导致的滚动位置计算误差。
 *
 * 原理：Virtualizer 的 estimateSize 回调优先读取 item.size 属性来获知高度，
 * 如果没有才会使用 itemHeight 的保底值。
 */

import {imageFormatConfig2 as imageFormatConfig} from './constants';

/**
 * 计算图片网格的高度
 *
 * @param {number} imageCount - 图片数量
 * @param {string} ratio - 纵横比（如 '1:1', '16:9' 等）
 * @returns {number} 图片网格的高度（px）
 */
function calculateImagesGridHeight(imageCount, ratio) {
  if (imageCount <= 0) {
    return 0;
  }

  // 获取该比例的配置
  const config = imageFormatConfig[ratio] || imageFormatConfig['1:1'];

  // 计算行数
  const rowCount = Math.ceil(imageCount / config.column);

  // 计算总高度 = (单张图片高度 × 行数) + (行间距 × (行数-1))
  const gridHeight = rowCount * config.height + (rowCount - 1) * config.gap;

  return gridHeight;
}

/**
 * 从预解析的 extData 中提取预期图片数量
 *
 * @param {Object} extData - 预解析的数据对象
 * @param {string} imageGenerateType - 任务类型（如 GENERATE, CUT_TOOL, EXPAND_TOOL）
 * @returns {number} 预期图片数量
 */
function extractExpectedImageCountFromData(extData = {}, imageGenerateType = '') {
  // 扩图和抠图始终返回1（单结果任务）
  if (imageGenerateType?.includes('CUT_TOOL') || imageGenerateType?.includes('EXPAND_TOOL')) {
    return 1;
  }
  // 普通生成：优先使用 targetCount，次选 generateCount，最后默认 4
  return extData.params?.targetCount || extData.params?.generateCount || 4;
}

/**
 * 从预解析的 extData 中提取比例信息
 *
 * @param {Object} extData - 预解析的数据对象
 * @param {string} imageGenerateType - 任务类型
 * @returns {string} 比例（如 '1:1', '16:9' 等）
 */
function extractRatioFromData(extData = {}, imageGenerateType = '') {
  // AGENT_EXPAND_TOOL
  if (extData.extendFormData?.sizeValue) {
    return extData.extendFormData.sizeValue;
  }

  // AGENT_CUT_TOOL / AGENT_IMAGE_GENERATE
  if (extData.params?.ratio) {
    return extData.params.ratio;
  }

  return '1:1';
}

/**
 * 计算任务卡片的虚拟滚动高度
 *
 * @param {Object} item - 任务卡片数据，包含以下字段：
 *   - imageGenerateType: 卡片类型（GENERATE/CUT_TOOL/EXPAND_TOOL）
 *   - showDateLabel: 是否显示日期标签
 *   - status: 卡片状态（1-生成中, 2-成功, 3-失败, 5-取消）
 *   - records: 图片数组
 *   - extData: 预解析的参数对象（新增，避免重复解析）
 *   - ext: 序列化的参数（兼容旧代码，不再推荐使用）
 *   - task: 任务信息，包含 ratioList、agentContent、referenceImages、prompt 等
 * @returns {number} 卡片总高度（px）
 */
export const calculateCardSize = item => {
  const {imageGenerateType, showDateLabel, status, records = [], extData = {}, task = {}} = item;

  let height = 0;

  if (imageGenerateType?.includes('GENERATE')) {
    // ===== TaskCardGenerate（图片生成卡片）=====
    // 精确计算：日期 + 参考图 + Prompt + 意图理解 + 图片网格 + 操作栏 + 失败提示 + 底部margin

    // 1️⃣ 日期栏（可选）
    // - line-height: 28px + margin-bottom: 12px
    if (showDateLabel) {
      height += 28 + 12;
    }

    // 2️⃣ 参考图片（可选，需判断数组是否非空）
    // - 高度: 36px + margin-bottom: 12px
    const referenceImagesCount = task.referenceImages?.length || 0;
    if (referenceImagesCount > 0) {
      height += 36 + 12;
    }

    // 3️⃣ Prompt 区（必有）
    // - padding: 12px 16px → 上下各 12px = 24px
    // - line-height: 26px
    // - margin-bottom: 48px
    // - 按 50 字/行估计（中文 14px/字，容器约 ~730px 内容宽度）
    const promptText = task.prompt || '';
    const promptLines = Math.ceil((promptText.length || 1) / 50);
    height += 24 + (promptLines * 26) + 48;

    // 4️⃣ 意图理解区（可选，可能有 0/1/2 个部分）
    // - status=1(生成中): 始终计算意图高度(展开状态)
    // - 其他状态: 默认折叠，不计入高度
    let intentHeight = 0;

    // agentContent 可能是 JSON 字符串，需要先解析
    let agentData = task.agentContent;
    if (typeof agentData === 'string' && agentData) {
      try {
        agentData = JSON.parse(agentData);
      }
 catch (e) {
        agentData = null;
      }
    }

    // ✨ 意图理解区的高度计算规则:
    // - status=1(生成中): 始终计算意图高度(展开状态)
    // - 其他状态: 默认折叠,意图高度为0
    // 注: 用户手动展开后,组件会调用 toggleIntentCollapse 重新计算高度
    const shouldShowIntentContent = status === 1;

    if (shouldShowIntentContent && agentData) {
      // 意图识别（可能无）
      const detectionText = agentData?.intent_recognition_result?.intent_recognition;
      if (detectionText) {
        // intent 区域是 block 布局，占满容器全宽（~830px），每行约 75 字符
        // 不同于 prompt 的 inline-block（较窄，用 50 字/行）
        const detectionLines = Math.ceil(detectionText.length / 75);
        // header padding-top(10) + label(20) + header padding-bottom(10) + content(lines*26)
        // = 40 + (detectionLines * 26)
        intentHeight += 40 + (detectionLines * 26);
      }

      // 意图分析（新结构：reasoning_display）
      if (agentData?.intent_parsing_result?.reasoning_display) {
        const {summary, intent_analysis} = agentData.intent_parsing_result.reasoning_display;
        let analysisHeight = 40; // header padding + label

        // Summary 文本高度
        if (summary) {
          const summaryLines = Math.ceil(summary.length / 75);
          analysisHeight += summaryLines * 26;
        }

        // 列表高度（每项约 28px：24px 行高 + 4px 间距）
        if (Array.isArray(intent_analysis) && intent_analysis.length > 0) {
          analysisHeight += 8; // 列表顶部间距
          analysisHeight += intent_analysis.length * 28; // 每项高度
        }

        intentHeight += analysisHeight;
      }
    }

    // 只有当有意图内容时才加入总高度
    if (intentHeight > 0) {
      intentHeight += 12; // intent-section margin-bottom
      height += intentHeight;
    }

    // 4️⃣.1 意图折叠按钮（条件显示）
    // 显示条件: 有意图内容 且 status不是1(生成中)或3(失败)
    if (shouldShowIntentContent && intentHeight > 0 && status !== 1 && status !== 3) {
      height += 32; // intent-collapse-btn: line-height(20) + padding(~12) = 32px
    }

    // 5️⃣ 图片网格（动态高度，但可能为 0）
    // - 取消状态（status=5）：不显示图片
    // - 失败状态（status=3）：不显示图片
    // - 生成中（status=1）：根据意图识别类型判断
    //   * res === 'need_parsing': 需要 intent_parsing_result 才显示
    //   * res !== 'need_parsing' (如 direct_tool): 只需 intent_recognition_result 即可显示
    //   * 无意图识别结果: 不显示
    // - 其他状态：显示图片
    if (status === 5 || status === 3) {
      height += 0;
    }
    else if (status === 1) {
      // 生成中状态：判断意图分析是否完成
      const recognitionRes = agentData?.intent_recognition_result?.route || '';
      const hasIntentRecognition = !!agentData?.intent_recognition_result?.intent_recognition;
      // ✅ 检查新结构的意图分析
      const hasIntentParsing = !!agentData?.intent_parsing_result?.reasoning_display;

      // 判断是否应该显示图片网格
      let shouldShowGrid = false;
      if (recognitionRes === 'need_parsing') {
        // need_parsing 类型：需要意图分析完成
        shouldShowGrid = hasIntentParsing;
      }
      else if (hasIntentRecognition) {
        // 其他类型（如 direct_tool）：只需意图识别完成
        shouldShowGrid = true;
      }

      if (shouldShowGrid) {
        const imageCount = records.length || extractExpectedImageCountFromData(extData, imageGenerateType);
        const ratio = extractRatioFromData(extData, imageGenerateType);
        const gridHeight = calculateImagesGridHeight(imageCount, ratio);
        height += gridHeight + 8;
      }
    }
    else {
      // 其他状态（成功等）：直接显示
      const imageCount = records.length || extractExpectedImageCountFromData(extData, imageGenerateType);
      const ratio = extractRatioFromData(extData, imageGenerateType);
      const gridHeight = calculateImagesGridHeight(imageCount, ratio);
      height += gridHeight + 8;
    }

    // 6️⃣ 操作栏
    // - padding: 16px 24px
    // - 实际高度: 36px
    height += 36;

    // 7️⃣ 失败状态文案（仅 status=3）
    // - status-error-top: line-height 40px
    // - status-error-desc: line-height 20px
    // - margin-bottom: 12px
    if (status === 3) {
      height += 72; // status-error: top(40) + desc(20) + margin(12) = 72px
    }

    // 8️⃣ 卡片底部 margin
    // - margin-bottom: 52px
    height += 52;

    // 调试日志
    let gridHeightDebug = 0;
    if (status === 5 || status === 3) {
      gridHeightDebug = 0;
    }
    else if (status === 1) {
      const recognitionRes = agentData?.intent_recognition_result?.route || '';
      const hasIntentRecognition = !!agentData?.intent_recognition_result?.intent_recognition;
      // ✅ 检查新结构的意图分析
      const hasIntentParsing = !!agentData?.intent_parsing_result?.reasoning_display;
      let shouldShowGrid = false;
      if (recognitionRes === 'need_parsing') {
        shouldShowGrid = hasIntentParsing;
      }
      else if (hasIntentRecognition) {
        shouldShowGrid = true;
      }
      if (shouldShowGrid) {
        gridHeightDebug = calculateImagesGridHeight(
          records.length || extractExpectedImageCountFromData(extData, imageGenerateType),
          extractRatioFromData(extData, imageGenerateType)
        ) + 8;
      }
    }
    else {
      gridHeightDebug = calculateImagesGridHeight(
        records.length || extractExpectedImageCountFromData(extData, imageGenerateType),
        extractRatioFromData(extData, imageGenerateType)
      ) + 8;
    }
    console.log(
      `📊 Generate 卡片高度: 日期=${showDateLabel ? 40 : 0}, 参考图=${
        referenceImagesCount > 0 ? 48 : 0
      }, Prompt=${24 + promptLines * 26 + 48}, 意图=${intentHeight}, 折叠按钮=${
        shouldShowIntentContent && intentHeight > 0 && status !== 1 && status !== 3 ? 32 : 0
      }, 图片=${gridHeightDebug}, 操作=36, 失败=${
        status === 3 ? 72 : 0
      }, 总计=${height}px`
    );

  }
  else if (
    imageGenerateType?.includes('CUT_TOOL')
    || imageGenerateType?.includes('EXPAND_TOOL')
  ) {
    // ===== TaskCardCutout / TaskCardExpand（抠图/扩图卡片）=====
    // 简化计算：日期 + 头部 + 图片网格 + 失败提示 + 底部margin

    // 1️⃣ 日期栏（可选）
    if (showDateLabel) {
      height += 28 + 12;
    }

    // 2️⃣ 卡片头部
    // - flex 布局，最高子元素 line-height: 26px + margin-bottom: 12px
    height += 26 + 12;

    // 3️⃣ 图片网格（动态高度）
    // - 取消状态（status=5）不显示
    // - 其他状态（包括 loading 骨架和已完成图片）都有高度
    if (status !== 5) {
      // loading 态（status=1）也渲染了 skeleton-item，高度同1张图片
      const imageCount = (records && records.length > 0)
        ? records.length
        : extractExpectedImageCountFromData(extData, imageGenerateType);
      const ratio = extractRatioFromData(extData, imageGenerateType);
      const gridHeight = calculateImagesGridHeight(imageCount, ratio);
      height += gridHeight;
    }

    // 4️⃣ 失败状态文案（仅 status=3）
    if (status === 3) {
      height += 22;
    }

    // 5️⃣ 卡片底部 margin
    height += 52;

  }
  else {
    // ===== 未知类型的降级方案 =====
    height += 400;
  }

  return height;
};
